import React, { useState } from 'react';
import { Box, Grid, Paper, Typography, useTheme, alpha } from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import BoxDetails from './BoxDetails';
import BoxEdit from './BoxEdit';
import { BoxState, BoxType, GetRatesContextType, SingleBoxPropsType } from '../Type';
import CopyBox from './CopyBox';
import DashedBtn from './DashedBtn';
import GetRatesContext from '../../../context/GetRates';
import { defaultBox } from '../Constant';
import Divider from '@Components/Divider';
import SbTextField from '@Components/Textfield';
import BoxCountValidation from '../RatesInput/BoxCountValidation/BoxDelete';
import { boxCountSchema } from './Schema';
import updateBoxId from '@Services/updateBoxId';

const DefaultBoxState = {
  weight: '',
  length: '',
  width: '',
  height: '',
  boxCopyCount: 0,
};

function SingleBoxView({ data }: SingleBoxPropsType) {
  const theme = useTheme();
  const [boxState, setBoxState] = useState<BoxState>('details');

  return (
    <Grid item xs={12} md={6} lg={4} xl={3}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: '#ffffff',
          transition: 'all 0.2s ease-in-out',
          minHeight: '180px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          '&:hover': {
            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.06)',
            borderColor: alpha(theme.palette.primary.main, 0.35),
          },
        }}
      >
        {boxState === 'edit' ? (
          <BoxEdit setBoxState={setBoxState} data={data} />
        ) : boxState === 'copy' ? (
          <CopyBox setBoxState={setBoxState} data={data} />
        ) : (
          <BoxDetails setBoxState={setBoxState} data={data} />
        )}
      </Paper>
    </Grid>
  );
}

function BoxWise() {
  const theme = useTheme();
  const { boxes, setBoxes, boxCount, setBoxCount } = React.useContext(GetRatesContext) as GetRatesContextType;
  const [boxCountValidation, setBoxCountValidation] = useState<boolean>(false);
  const [boxCountError, setBoxCountError] = useState<string>('');

  function handleAddBox() {
    const boxesTemp = structuredClone(boxes);
    boxesTemp.push({ ...DefaultBoxState, id: boxesTemp.length.toString() });
    const arr = updateBoxId<BoxType>(boxesTemp, defaultBox, 0);
    setBoxes([...arr]);
  }

  function validateBoxCount(value: number) {
    const result = boxCountSchema.validate(value);
    const errorMessage = result.error ? result.error.details[0].message : '';
    setBoxCountError(errorMessage);
  }

  function handleOnBoxCountChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;
    const _boxCount = parseInt(value);
    validateBoxCount(_boxCount);
    setBoxCount(_boxCount);
    if (!_boxCount) return;
    if (_boxCount < boxes.length) {
      setBoxCountValidation(true);
    } else {
      setBoxes([
        ...boxes,
        ...Array(_boxCount - boxes.length)
          .fill(0)
          .map((_x, ind) => ({ ...defaultBox, id: (boxes.length + ind).toString() })),
      ]);
    }
  }

  function handleOnBoxCountBlur() {
    setBoxCountError('');
    if (boxes.length !== boxCount) {
      setBoxCount(boxes.length);
    }
  }

  return (
    <>
      <Grid item xs={12} className="my-3">
        <Divider />
      </Grid>

      {/* Header with Title & Box Counter */}
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Inventory2OutlinedIcon sx={{ fontSize: 22, color: theme.palette.primary.main }} />
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', color: 'text.primary' }}>
            Box-wise Details
          </Typography>
          <Box
            sx={{
              fontSize: '0.75rem',
              fontWeight: 700,
              px: 1,
              py: 0.25,
              borderRadius: '12px',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
            }}
          >
            {boxes.length} {boxes.length === 1 ? 'Box' : 'Boxes'}
          </Box>
        </Box>

        <Box sx={{ width: { xs: '100%', sm: '180px' } }}>
          <SbTextField
            placeholder="No. of Boxes"
            label="Total Boxes"
            type="number"
            value={boxCount ? boxCount.toString() : ''}
            onChange={handleOnBoxCountChange}
            onBlur={handleOnBoxCountBlur}
            name="boxCount"
            error={boxCountError}
          />
        </Box>
      </Box>

      {/* Boxes Grid */}
      <Box sx={{ flexGrow: 1 }}>
        <Grid container spacing={{ xs: 2, md: 3 }}>
          {boxes.map(box => (
            <SingleBoxView key={box.id} data={box} />
          ))}
        </Grid>
        <Grid container className="mb-2" marginTop={'1.5rem'} justifyContent={'center'}>
          <Grid item xs={12} sm={8} md={6}>
            <DashedBtn label="Add a Box" onClick={handleAddBox} />
          </Grid>
        </Grid>
      </Box>

      <BoxCountValidation
        open={boxCountValidation}
        onClose={() => setBoxCountValidation(false)}
        onConfirm={() => setBoxCountValidation(false)}
      />
    </>
  );
}

export default BoxWise;
